/**
 * Pitch math: key names → pitch classes, scale degrees → MIDI notes → Hz.
 *
 * Everything musical in the audio module works in *scale degrees* relative to
 * the theme's root (0 = tonic, 1 = next scale note, −1 = the note below the
 * tonic, scaleLength = the tonic an octave up). This keeps melodies and
 * chords in key for every theme without per-key tables.
 *
 * Pure and DOM-free (unit-tested in node).
 */
import type { ThemeAudio } from '@dropstack/core';

export type ScaleName = ThemeAudio['scale'];

/** Semitone offsets from the root for one octave of each scale. */
export const SCALE_INTERVALS: Readonly<Record<ScaleName, readonly number[]>> = {
  majorPentatonic: [0, 2, 4, 7, 9],
  major: [0, 2, 4, 5, 7, 9, 11],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
};

/**
 * Registers (lowest MIDI note allowed for the root) of each musical part, so
 * every theme sits in the same comfortable range whatever its key.
 * The root of a part is the first note of the key's pitch class at or above
 * its floor, i.e. within [floor, floor + 11].
 */
export const REGISTER = {
  /** Lead melody root: C4…B4 (the melody wanders from 2 degrees below to ~1.5 octaves above). */
  lead: 60,
  /** Pad chord roots: C3…B3. */
  pad: 48,
  /** Soft bass: C2…B2. */
  bass: 36,
  /** Pitched SFX (chimes, arpeggios) are tuned to the theme key: G4…F#5. */
  sfx: 67,
} as const;

const LETTER_PITCH: Readonly<Record<string, number>> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/**
 * Pitch class (0 = C … 11 = B) of a key name such as 'C', 'F#', 'Bb', 'e♭' or
 * 'G major'. Unknown names fall back to C, so bad theme data never breaks audio.
 */
export function pitchClass(key: string): number {
  const m = /^\s*([A-Ga-g])([#♯b♭]*)/.exec(key);
  if (!m) return 0;
  let pc = LETTER_PITCH[m[1]!.toUpperCase()]!;
  for (const accidental of m[2]!) pc += accidental === '#' || accidental === '♯' ? 1 : -1;
  return mod(pc, 12);
}

/** Equal-tempered frequency of a MIDI note (A4 = 69 = 440 Hz). */
export function midiToHz(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12);
}

/** Lowest MIDI note ≥ `floor` whose pitch class is `pc`. */
export function midiAtOrAbove(pc: number, floor: number): number {
  return floor + mod(pc - floor, 12);
}

/** Root MIDI note of `key` in the given register (see REGISTER). */
export function rootMidi(key: string, register: number): number {
  return midiAtOrAbove(pitchClass(key), register);
}

/** Number of notes per octave in the scale (5 for pentatonic, 7 otherwise). */
export function scaleLength(scale: ScaleName): number {
  return SCALE_INTERVALS[scale].length;
}

/** MIDI note of a scale degree. Degrees outside 0…length−1 wrap into neighbouring octaves. */
export function degreeToMidi(degree: number, root: number, scale: ScaleName): number {
  const intervals = SCALE_INTERVALS[scale];
  const n = intervals.length;
  const d = Math.round(degree);
  const octave = Math.floor(d / n);
  return root + octave * 12 + intervals[d - octave * n]!;
}

/** Frequency (Hz) of a scale degree. */
export function degreeToHz(degree: number, root: number, scale: ScaleName): number {
  return midiToHz(degreeToMidi(degree, root, scale));
}

/** Frequencies for degrees `from`…`to` inclusive (handy for tables and tests). */
export function scaleFrequencyTable(key: string, scale: ScaleName, register: number, from: number, to: number): number[] {
  const root = rootMidi(key, register);
  const out: number[] = [];
  for (let d = from; d <= to; d++) out.push(degreeToHz(d, root, scale));
  return out;
}

/** Mathematical modulo (always non-negative for positive `m`). */
export function mod(x: number, m: number): number {
  return ((x % m) + m) % m;
}
